import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import BundleItem from '../BundleItem';
import messages from './messages';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import {
  setSelectedAttributesValues,
  setSelectedVariantsForBundleItemProducts,
} from '../../../slices/bundlePdp';
import { getVariantWithLowestPrice } from '../../../utils/variants';
import useQueryParams from '../../../hooks/useQueryParams/useQueryParams';

import VariantSelection from '../../pdpV2/VariantSelection';
import { productTypeToSingularProductNameConverter } from '../../../utils/product';

import styles from './BundleOptions.module.scss';
import {
  ATTRIBUTE_MAP,
  COLOR_NAME,
  DEPTH_ATTRIBUTE_NAME,
  IGNORE_COLOR_PRODUCT_KEYS,
  ordinalAsLiteral,
  PILLOW_HEIGHT_NAME,
  PILLOW_SIZE_NAME,
  PILLOW_TYPE,
} from './constants';
import { SIZE_NAME } from '../BundleItemProductCard/constants';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import {
  TWIN_SIZE_KEY,
  TWIN_XL_SIZE_KEY,
  MATTRESSES_PRODUCT_TYPE,
  PILLOWS_PRODUCT_TYPE,
} from '../../../utils/common/constants';
import { BundlePdpInStore } from '../../../slices/bundlePdp/types';
import { BundleItem as BundleItemType } from '../BundleItem/types';
import { BundleProductVariant } from './types';
import { BundleDrupalSettings } from '../AddToCartBundlePdpContainer/types';
import { useThunkDispatch } from '../../../hooks/useThunkDispatch/useThunkDispatch';

const getProductOrdinalLegend = ({
  productType,
  ordinal,
}: {
  productType: string;
  ordinal: number;
}) =>
  ordinalAsLiteral[ordinal as keyof typeof ordinalAsLiteral]
    ? `${ordinalAsLiteral[ordinal as keyof typeof ordinalAsLiteral]} ${productType}`
    : `${productType} ${ordinal}`;

export const getBundleItemVariantSelectionAttributesUpperTitle = ({
  bundleItem,
  product,
  index,
}: {
  bundleItem: BundleItemType;
  product: BundleDrupalSettings['product'];
  index: number;
}) =>
  bundleItem?.quantity > 1 && !product?.use_size_matching_logic
    ? getProductOrdinalLegend({
        ordinal: index + 1,
        productType: productTypeToSingularProductNameConverter(
          bundleItem?.product?.productType,
        ),
      })
    : '';

const BundleOptions = () => {
  const { bundle: bundleDraft, product } =
    useDrupalSettings() as BundleDrupalSettings;
  const { publish } = drupalPubSubService;
  const { formatMessage } = useIntl();

  const [bundleLoaded, setBundleLoaded] = useState(false);
  const { selectedVariantsForBundleItemProducts, selectedAttributesValues } =
    useSelector((state: { bundlePdp: BundlePdpInStore }) => state.bundlePdp);

  const shouldUseLowestPrice = !!product?.use_lowest_price;

  const [sizeQueryParam] = useQueryParams([SIZE_NAME]);
  // Flag to track if the size query param logic has been processed to ensure it only runs once on initial load.
  const hasProcessedSizeQueryParam = useRef(false);

  const dispatch = useThunkDispatch();

  const withProductType = (
    variant: BundleProductVariant | undefined,
    productType: string | undefined,
  ) => (variant ? { ...variant, productType } : variant);

  const isTwinBundle =
    bundleDraft?.[0]?.product?.productType === MATTRESSES_PRODUCT_TYPE &&
    (selectedAttributesValues?.size === TWIN_SIZE_KEY ||
      selectedAttributesValues?.size === TWIN_XL_SIZE_KEY);

  const shouldReducePillowQtyForTwin =
    isTwinBundle && !product?.keep_pillow_qty_on_twin;

  const bundle = useMemo<BundleItemType[]>(
    () =>
      bundleDraft.map((bundleItem: BundleItemType) => ({
        ...bundleItem,
        quantity:
          bundleItem?.product?.productType === PILLOWS_PRODUCT_TYPE &&
          shouldReducePillowQtyForTwin
            ? 1
            : bundleItem.quantity,
      })),
    [bundleDraft, shouldReducePillowQtyForTwin],
  );

  const bundleHasMattresses = bundle.some(
    (bundleItem: BundleItemType) =>
      bundleItem?.product?.productType === MATTRESSES_PRODUCT_TYPE,
  );

  useEffect(() => {
    const getDefaultSelectedSKUForBundleItem = ({
      bundleItem,
    }: {
      bundleItem: BundleItemType;
    }) =>
      ((sizeQueryParam &&
        bundleItem?.product?.variants?.find((productVariant) =>
          productVariant.attributes?.some(
            (attr: { name: string; key: string }) =>
              attr.name === SIZE_NAME && attr.key === sizeQueryParam,
          ),
        )) ??
        (shouldUseLowestPrice
          ? getVariantWithLowestPrice(bundleItem?.product?.variants)
          : bundleItem?.product?.variants?.find(
              (productVariant) => productVariant.masterVariant === true,
            )) ??
        bundleItem?.product?.variants[0]) as BundleProductVariant;

    if (bundle && !bundleLoaded) {
      let initiallySelectedAttributeValues: { size?: string; color?: string } =
        {};

      const newSelectedVariantsForBundleItemProducts = Object.fromEntries(
        bundle.map((bundleItem: BundleItemType) => [
          bundleItem?.product?.key,
          [...Array(bundleItem?.quantity).keys()].map(() => {
            const defaultVariant = getDefaultSelectedSKUForBundleItem({
              bundleItem,
            });
            const productType = bundleItem?.product?.productType;
            const defaultVariantWithProductType = withProductType(
              defaultVariant,
              productType,
            );

            defaultVariantWithProductType?.attributes?.forEach(
              (attr: { name: string; key: string }) => {
                if (
                  attr.name.includes(SIZE_NAME) &&
                  attr.name !== PILLOW_SIZE_NAME &&
                  !initiallySelectedAttributeValues?.size
                ) {
                  initiallySelectedAttributeValues = {
                    ...initiallySelectedAttributeValues,
                    size: attr.key,
                  };
                }
                if (
                  !IGNORE_COLOR_PRODUCT_KEYS.includes(
                    bundleItem?.product.key,
                  ) &&
                  attr.name === COLOR_NAME
                ) {
                  initiallySelectedAttributeValues = {
                    ...initiallySelectedAttributeValues,
                    color: attr.key,
                  };
                }
              },
            );
            return defaultVariantWithProductType;
          }),
        ]),
      );
      dispatch(setSelectedAttributesValues(initiallySelectedAttributeValues));
      dispatch(
        setSelectedVariantsForBundleItemProducts({
          selectedVariantsForBundleItemProducts:
            newSelectedVariantsForBundleItemProducts,
        }),
      );
      if (
        bundle?.[0]?.product?.productType === MATTRESSES_PRODUCT_TYPE &&
        !Object.keys(selectedAttributesValues)?.length
      ) {
        setBundleLoaded(false);
      } else {
        setBundleLoaded(true);
      }
    }
  }, [
    bundle,
    bundleLoaded,
    dispatch,
    selectedAttributesValues,
    shouldUseLowestPrice,
    sizeQueryParam,
  ]);

  const bundleItemVariantSelectionChangeHandler = ({
    productKey,
    selectedVariantSKU,
  }: {
    productKey: string;
    selectedVariantSKU: string;
  }) => {
    let newSelectedAttributeValues = selectedAttributesValues;
    let newSelectedVariantsForBundleItemProducts = Object.fromEntries(
      Object.entries(selectedVariantsForBundleItemProducts).map(
        ([bundleItemProductKey, selectedVariantsForBundleItems]) => {
          if (bundleItemProductKey === productKey) {
            let newSelectedVariantsForBundleItems =
              selectedVariantsForBundleItems.map(() => {
                const bundleItemProduct = bundle.find(
                  (bundleItem: BundleItemType) =>
                    bundleItem?.product?.key === productKey,
                )?.product;
                const selectedVariant = bundleItemProduct?.variants?.find(
                  (variant: BundleProductVariant) =>
                    variant?.sku === selectedVariantSKU,
                );
                return withProductType(
                  selectedVariant,
                  bundleItemProduct?.productType,
                );
              });

            if (
              selectedVariantsForBundleItems?.[0]?.productType === PILLOWS_PRODUCT_TYPE &&
              shouldReducePillowQtyForTwin &&
              newSelectedVariantsForBundleItems?.length
            ) {
              const newQuantity =
                bundleDraft?.find(
                  (bundleItem: BundleItemType) =>
                    bundleItem?.product?.key === productKey,
                )?.quantity ?? 1;
              newSelectedVariantsForBundleItems = [
                ...Array(newQuantity).keys(),
              ].map(() => newSelectedVariantsForBundleItems?.[0]);
            }
            publish({
              subscription: 'onVariantChange',
              parameters: newSelectedVariantsForBundleItems?.[0],
            });
            // Only the primary bundle item's own "size" vocabulary defines the
            // bundle-wide tracked size (ATTRIBUTE_MAP translates it to every
            // other item's equivalent). Other items — e.g. Sheets — carry a
            // "size" attribute in their OWN vocabulary (bucketed values like
            // "full-full-xl-queen"), which would otherwise clobber the tracked
            // mattress size the moment any non-primary item's variant changes
            // (e.g. just picking a different Sheets color), incorrectly
            // resetting isTwinBundle/shouldReducePillowQtyForTwin.
            const isPrimaryBundleItem =
              bundleItemProductKey === bundleDraft?.[0]?.product?.key;
            newSelectedVariantsForBundleItems?.[0]?.attributes?.forEach(
              (attr) => {
                if (
                  isPrimaryBundleItem &&
                  attr.name.includes(SIZE_NAME) &&
                  attr.name !== PILLOW_SIZE_NAME
                ) {
                  newSelectedAttributeValues = {
                    ...newSelectedAttributeValues,
                    size: attr.key,
                  };
                }
                if (
                  !IGNORE_COLOR_PRODUCT_KEYS.includes(bundleItemProductKey) &&
                  attr.name === COLOR_NAME
                ) {
                  newSelectedAttributeValues = {
                    ...newSelectedAttributeValues,
                    color: attr.key,
                  };
                }
                if (attr.name === DEPTH_ATTRIBUTE_NAME) {
                  newSelectedAttributeValues = {
                    ...newSelectedAttributeValues,
                    depth: attr.key,
                  };
                }
              },
            );
            return [bundleItemProductKey, newSelectedVariantsForBundleItems];
          }

          if (product?.use_size_matching_logic) {
            const matchedSelectedVariantsForBundleItems =
              selectedVariantsForBundleItems.map(
                (
                  currentSelectedVariant: BundleProductVariant | undefined,
                ): BundleProductVariant | undefined => {
                  const matchingBundleItemProduct = bundle?.find(
                    (bundleItem: BundleItemType) =>
                      bundleItem?.product?.key === bundleItemProductKey,
                  )?.product;
                  // Preserve the pillow height currently selected so changing
                  // the mattress size only swaps the pillow size, not its height.
                  const currentPillowHeight =
                    currentSelectedVariant?.attributes?.find(
                      (attr) => attr.name === PILLOW_HEIGHT_NAME,
                    )?.key;
                  const attributeMapToFilterBy =
                    ATTRIBUTE_MAP.find(
                      (productMap) =>
                        productMap?.bundleProductProductTypeKey ===
                          matchingBundleItemProduct?.productType &&
                        matchingBundleItemProduct.available_attributes[
                          productMap.aovProductAttribute
                        ],
                    )?.values.find(
                      (attr) =>
                        attr.targetValue === newSelectedAttributeValues?.size,
                    )?.aovEquivalency ?? [newSelectedAttributeValues?.size];
                  const matchedSelectedVariants =
                    matchingBundleItemProduct?.variants?.filter(
                      (variant: BundleProductVariant) =>
                        variant?.attributes?.some(
                          (attr) => attributeMapToFilterBy.includes(attr.key),
                        ),
                    );

                  if (
                    !IGNORE_COLOR_PRODUCT_KEYS.includes(bundleItemProductKey) &&
                    matchingBundleItemProduct?.available_attributes?.color &&
                    !(
                      PILLOW_SIZE_NAME in
                      matchingBundleItemProduct.available_attributes
                    )
                  ) {
                    let matchedVariant = matchedSelectedVariants?.find(
                      (variant: BundleProductVariant) =>
                        variant?.attributes?.find(
                          (attr) => attr?.name === COLOR_NAME,
                        )?.key === newSelectedAttributeValues.color,
                    );
                    if (matchedVariant?.isOutOfStock) {
                      matchedVariant = matchedSelectedVariants?.find(
                        (variant: BundleProductVariant) =>
                          !variant.isOutOfStock,
                      );
                    }
                    const matchedVariantWithProductType = withProductType(
                      matchedVariant,
                      matchingBundleItemProduct?.productType,
                    );
                    publish({
                      subscription: 'onVariantChange',
                      parameters: matchedVariantWithProductType,
                    });
                    return matchedVariantWithProductType;
                  }

                  const doesTheSelectedProductHaveDepthAttribute =
                    !!matchingBundleItemProduct?.available_attributes?.[
                      DEPTH_ATTRIBUTE_NAME
                    ];

                  if (
                    newSelectedAttributeValues?.depth &&
                    doesTheSelectedProductHaveDepthAttribute
                  ) {
                    return withProductType(
                      matchedSelectedVariants?.find(
                      (variant: BundleProductVariant) =>
                        variant?.attributes?.some(
                          (attr) => attributeMapToFilterBy.includes(attr.key),
                        ) &&
                        variant?.attributes?.some(
                          (attr) =>
                            attr.key === newSelectedAttributeValues.depth,
                        ),
                      ),
                      matchingBundleItemProduct?.productType,
                    );
                  }

                  const matchedSelectedVariant =
                    (currentPillowHeight &&
                      matchedSelectedVariants?.find(
                        (variant: BundleProductVariant) =>
                          variant?.attributes?.some((attr) =>
                            attributeMapToFilterBy.includes(attr.key),
                          ) &&
                          variant?.attributes?.some(
                            (attr) =>
                              attr.name === PILLOW_HEIGHT_NAME &&
                              attr.key === currentPillowHeight,
                          ),
                      )) ||
                    matchedSelectedVariants?.find(
                      (variant: BundleProductVariant) =>
                        variant?.attributes?.find((attr) =>
                          attributeMapToFilterBy.includes(attr.key),
                        ),
                    );
                  const matchedSelectedVariantWithProductType = withProductType(
                    matchedSelectedVariant,
                    matchingBundleItemProduct?.productType,
                  );
                  publish({
                    subscription: 'onVariantChange',
                    parameters: matchedSelectedVariantWithProductType,
                  });
                  return matchedSelectedVariantWithProductType;
                },
              );

            return [
              bundleItemProductKey,
              matchedSelectedVariantsForBundleItems?.[0]
                ? matchedSelectedVariantsForBundleItems
                : selectedVariantsForBundleItems,
            ];
          }

          return [bundleItemProductKey, selectedVariantsForBundleItems];
        },
      ),
    );

    const pillowProductKeys = bundleDraft
      ?.filter(
        (bundleItem: BundleItemType) =>
          bundleItem?.product?.productType === PILLOWS_PRODUCT_TYPE,
      )
      .map((bundleItem: BundleItemType) => bundleItem?.product?.key)
      .filter((key): key is string => !!key);

    if (
      bundleDraft?.[0]?.product?.productType === MATTRESSES_PRODUCT_TYPE &&
      pillowProductKeys?.length
    ) {
      pillowProductKeys.forEach((pillowProductKey) => {
        if (!newSelectedVariantsForBundleItemProducts?.[pillowProductKey]) {
          return;
        }

        const isTwinSelection =
          newSelectedAttributeValues.size === TWIN_SIZE_KEY ||
          newSelectedAttributeValues.size === TWIN_XL_SIZE_KEY;

        if (isTwinSelection && !product?.keep_pillow_qty_on_twin) {
          newSelectedVariantsForBundleItemProducts = {
            ...newSelectedVariantsForBundleItemProducts,
            [pillowProductKey]:
              newSelectedVariantsForBundleItemProducts[pillowProductKey].slice(
                0,
                1,
              ),
          };
        } else {
          const quantity = bundleDraft?.find(
            (bundleItem: BundleItemType) =>
              bundleItem.product.key === pillowProductKey,
          )?.quantity;

          const pillowVariant =
            newSelectedVariantsForBundleItemProducts[pillowProductKey][0];

          if (
            pillowVariant &&
            'price' in pillowVariant &&
            'discount' in pillowVariant
          ) {
            newSelectedVariantsForBundleItemProducts = {
              ...newSelectedVariantsForBundleItemProducts,
              [pillowProductKey]: [...Array(quantity ?? 1).keys()].map(
                () => pillowVariant,
              ),
            };
          }
        }
      });
    }
    dispatch(setSelectedAttributesValues(newSelectedAttributeValues));
    dispatch(
      setSelectedVariantsForBundleItemProducts({
        selectedVariantsForBundleItemProducts:
          newSelectedVariantsForBundleItemProducts,
      }),
    );
  };

  const getFilteredBundle = () =>
    bundle.filter((bundleItem: BundleItemType, index: number) => {
      const hasColorAttribute =
        bundleItem?.product?.available_attributes?.color;
      const hasPillowHeightAttribute =
        bundleItem?.product?.available_attributes?.[PILLOW_HEIGHT_NAME];

      const availableAttributes = bundleItem?.product?.available_attributes;

      return (
        index === 0 ||
        (hasColorAttribute &&
          !!availableAttributes &&
          !(PILLOW_SIZE_NAME in availableAttributes)) ||
        hasPillowHeightAttribute
      );
    });

  const variantSelectionBundle = product?.use_size_matching_logic
    ? getFilteredBundle()
    : bundle;

  useEffect(() => {
    // Update variants for mattress products to match the requested size.
    if (
      !hasProcessedSizeQueryParam.current &&
      sizeQueryParam &&
      bundleHasMattresses &&
      Object.keys(selectedVariantsForBundleItemProducts).length > 0
    ) {
      // Only run once to prevent infinite loops.
      hasProcessedSizeQueryParam.current = true;
      const mattressBundleItem = bundle?.find(
        (bundleItem: BundleItemType) =>
          bundleItem?.product?.productType === MATTRESSES_PRODUCT_TYPE,
      );
      const mattressProductKey = mattressBundleItem?.product?.key;
      if (mattressProductKey) {
        const mattressVariant =
          selectedVariantsForBundleItemProducts[mattressProductKey]?.[0];
        if (mattressVariant) {
          bundleItemVariantSelectionChangeHandler({
            productKey: mattressProductKey,
            selectedVariantSKU: mattressVariant.sku,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantsForBundleItemProducts]);

  return (
    <div>
      {variantSelectionBundle?.map(
        (bundleItem: BundleItemType, index: number) => (
          <div
            key={`variant-selector-${bundleItem?.product?.key}`}
            data-testid={`bundle-variant-product-key-${bundleItem?.product?.key}`}
          >
            <VariantSelection
              hidePillowSize={
                bundleItem?.product?.productType === PILLOW_TYPE &&
                product?.use_size_matching_logic
              }
              productName={bundleItem?.product?.name}
              attributesExtraInfo={bundleItem?.product?.attribute_links}
              attributesUpperTitle={getBundleItemVariantSelectionAttributesUpperTitle(
                { bundleItem, product, index },
              )}
              attributeTitlePrefix='Sheet'
              productKey={bundleItem?.product?.key}
              defaultSelectedSKU={
                selectedVariantsForBundleItemProducts?.[
                  bundleItem?.product?.key
                ]?.[0]?.sku
              }
              // eslint-disable-next-line react/no-array-index-key
              key={`variant-selection-${bundleItem?.product?.key}-${index}`}
              variants={bundleItem?.product.variants}
              onSelectVariant={({ sku }: { sku: string }) => {
                bundleItemVariantSelectionChangeHandler({
                  productKey: bundleItem?.product?.key,
                  selectedVariantSKU: sku,
                });
              }}
              isBundlePdp
              isBundleSizeMatching={product?.use_size_matching_logic}
              isNonPrimaryBundleSelection={
                index > 0 &&
                bundleItem?.product?.productType !== PILLOW_TYPE &&
                product?.use_size_matching_logic
              }
              isShowingPriceDetails
              isShowingDiscountedPrices
            />
            {bundleItem.product.variants.length > 1 && (
              <div className={styles['bundle-options-divider']} />
            )}
          </div>
        ),
      )}
      <div className={styles['bundle-options-included-section-title']}>
        {formatMessage(messages.bundleIncludedSectionTitle)}
      </div>
      {bundle?.map((bundleItem: BundleItemType, index: number) => (
        <BundleItem
          bundleItem={bundleItem}
          isPrimaryBundle={index === 0}
          hidePrimaryDescription={product?.hide_primary_description ?? false}
        />
      ))}
      <div className={styles['bundle-options-divider']} />
    </div>
  );
};

export default BundleOptions;
